import { ChangeDetectionStrategy, Component, inject, Input, OnChanges, OnDestroy, signal, SimpleChanges } from '@angular/core';
import { fileTypeFromBuffer } from 'file-type';
import * as ExifReader from 'exifreader';
import { parseBlob } from 'music-metadata';
// @ts-expect-error - mammoth.browser is not properly typed
import { extractRawText } from 'mammoth/mammoth.browser';
import * as XLSX from 'xlsx';
import { FILE_TYPE, ResponseType } from '../../constants/general.constants';
import { GeneralHelpers } from '../../helpers/general.helper';
import { FileUnifiedService } from '../../services/file-unified/file-unified.service';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';

@Component({
  standalone: true,
  selector: 'app-file-info',
  templateUrl: './file-info.component.html',
  styleUrls: ['./file-info.component.scss'],
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    MatDividerModule,
    MatListModule,
    FlexLayoutModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FileInfoComponent implements OnChanges, OnDestroy {
  #fileUnifiedService = inject(FileUnifiedService);
  @Input() fileData!: any;
  fileInfo = signal<any>({});

  async ngOnChanges(changes: SimpleChanges) {
    if (changes['fileData'] && this.fileData) {
      await this.processFile();
    }
  }

  ngOnDestroy() {
    // Clean up any object URLs to prevent memory leaks
    const info = this.fileInfo();
    if (info.imageUrl) {
      URL.revokeObjectURL(info.imageUrl);
    }
    if (info.videoUrl) {
      URL.revokeObjectURL(info.videoUrl);
    }
    if (info.audioUrl) {
      URL.revokeObjectURL(info.audioUrl);
    }
  }

  // Convert blob to base64 URL using async/await
  private async getBase64Url(data: Blob | ArrayBuffer, fileType: string, fileName: string): Promise<string> {
    const base64Data = data instanceof ArrayBuffer ? GeneralHelpers.arrayBufferToBase64(data) : await GeneralHelpers.blobToBase64(data);
    const fileBase64Presentation = `data:${GeneralHelpers.getMimeType(
      fileType as FILE_TYPE,
      GeneralHelpers.fileExtensionFromString(fileName),
    )};base64,${base64Data}`;

    return fileBase64Presentation;
  }

  async processFile() {
    const file = this.fileData.data;

    if (!file) return;

    // Reset fileInfo object for new file
    const info: any = {
      name: file.name,
      size: (file.filesize / 1024).toFixed(2) + ' KB',
      uploadedAt: new Date(file.createdAt || file.modifiedAt).toISOString(),
      type: file.fileType || GeneralHelpers.fileExtensionFromString(file.name),
      imageUrl: null,
      videoUrl: null,
      audioUrl: null
    };

    if (!info.type) {
      try {
        const typeData = await fileTypeFromBuffer(file.data);
        info.type = typeData?.ext || 'unknown'
      } catch {
        info.type = 'unknown';
      }
    }

    try {
      if (this.isImage(file)) {
        const fileData = await this.#fileUnifiedService.getFile([file.name, ResponseType.Blob]);
        const processFile: Blob | ArrayBuffer = fileData instanceof Uint8Array ? fileData.buffer : fileData;
        info.imageUrl = await this.getBase64Url(processFile, info.type, file.name);
        await this.extractExifData(processFile, info);
      } else if (this.isVideo(file)) {
        const fileData = await this.#fileUnifiedService.getFile([file.name, ResponseType.Blob]);
        const processFile: Blob | ArrayBuffer = fileData instanceof Uint8Array ? fileData.buffer : fileData;
        info.videoUrl = await this.getBase64Url(processFile, info.type, file.name);
        this.extractVideoDuration(info);
      } else if (this.isAudio(file)) {
        const fileData = await this.#fileUnifiedService.getFile([file.name, ResponseType.Blob]);
        const processFile: Blob | ArrayBuffer = fileData instanceof Uint8Array ? fileData.buffer : fileData;
        info.audioUrl = await this.getBase64Url(processFile, info.type, file.name);
        await this.extractAudioMetadata(fileData, info);
      } else if (this.isWord(file)) {
        const fileData = await this.#fileUnifiedService.getFile([file.name, ResponseType.ArrayBuffer]);
        await this.extractWordText(fileData, info);
      } else if (this.isExcel(file)) {
        const fileData = await this.#fileUnifiedService.getFile([file.name, ResponseType.ArrayBuffer]);
        await this.extractExcelData(fileData, info);
      } else {
        let fileData = await this.#fileUnifiedService.getFile([file.name, ResponseType.ArrayBuffer]);

        if (fileData instanceof ArrayBuffer) {
          fileData = new Uint8Array(fileData);
        }

        await this.extractUnknownFileInfo(fileData, info);
      }
    } catch (error) {
      console.error('Error processing file:', error);
    }
    this.fileInfo.set(info);
  }

  async extractExifData(file: Blob | ArrayBuffer, info: any) {
    try {
      const tags = await this.loadExifTags(
        file instanceof ArrayBuffer ? new Blob([file]) : file
      );
      this.processExifData(tags, info);
    } catch (error) {
      console.warn('Error reading EXIF data:', error);
    }
  }

  private async loadExifTags(file: Blob) {
    return await ExifReader.load(
      await file.arrayBuffer(),
      { expanded: true, includeUnknown: true }
    );
  }

  private processExifData(tags: any, info: any) {
    const exifTags = tags.exif || {};
    const fileTags = tags.file || {};

    this.setBasicImageInfo(exifTags, fileTags, info);
    this.setCameraInfo(exifTags, info);
    this.setDateTimeInfo(exifTags, info);
    this.setGpsInfo(tags.gps, info);
    this.cleanupEmptyExifData(info);
  }

  private setBasicImageInfo(exifTags: any, fileTags: any, info: any) {
    info.orientation = exifTags.Orientation?.description;
    this.setImageDimensions(exifTags, fileTags, info);
  }

  private setImageDimensions(exifTags: any, fileTags: any, info: any) {
    const width = this.getDimensionValue(exifTags, fileTags, 'width');
    const height = this.getDimensionValue(exifTags, fileTags, 'height');

    if (width && height) {
      info.dimensions = `${width} x ${height} px`;
    }
  }

  private getDimensionValue(exifTags: any, fileTags: any, dimension: 'width' | 'height'): number | undefined {
    const exifKey = dimension === 'width' ? 'PixelXDimension' : 'PixelYDimension';
    const fileKey = dimension === 'width' ? 'Image Width' : 'Image Height';
    const fallbackKey = dimension === 'width' ? 'ImageWidth' : 'ImageLength';

    return exifTags[exifKey]?.value ||
      exifTags[fallbackKey]?.value ||
      fileTags[fileKey]?.value;
  }

  private setCameraInfo(exifTags: any, info: any) {
    const make = this.getTagValue(exifTags.Make);
    const model = this.getTagValue(exifTags.Model);

    if (make || model) {
      info.exif = {
        ...info.exif,
        Camera: [make, model].filter(Boolean).join(' ')
      };
    }
  }

  private setDateTimeInfo(exifTags: any, info: any) {
    const dateTime = this.getTagValue(exifTags.DateTime);
    const iso = this.getIsoValue(exifTags.ISOSpeedRatings);

    if (dateTime || iso) {
      info.exif = {
        ...info.exif,
        ...(dateTime && { DateTaken: dateTime }),
        ...(iso && { ISO: iso })
      };
    }
  }

  private getTagValue(tag: any): string {
    if (!tag) return '';
    return Array.isArray(tag.value) ? tag.value.join(' ') : tag.value?.toString() || '';
  }

  private getIsoValue(isoTag: any): string | undefined {
    if (!isoTag) return undefined;
    if (typeof isoTag.value === 'number') return isoTag.value.toString();
    if (Array.isArray(isoTag.value)) return isoTag.value.join(', ');
    return undefined;
  }

  private setGpsInfo(gpsTags: any, info: any) {
    if (!gpsTags) return;

    info.gps = {
      Latitude: gpsTags.Latitude,
      Longitude: gpsTags.Longitude,
      Altitude: gpsTags.Altitude
    };
  }

  private cleanupEmptyExifData(info: any) {
    if (info.exif) {
      Object.keys(info.exif).forEach(key => {
        if (!info.exif[key]) {
          delete info.exif[key];
        }
      });
    }
  }

  extractVideoDuration(info: any) {
    // Set default duration value immediately
    info.duration = 'Calculating...';

    const video = document.createElement('video');
    video.preload = 'metadata';

    // Set up error handling with cleaner error logging
    video.onerror = () => {
      console.warn('Video metadata loading failed');
      info.duration = 'Unable to determine';
    };

    // Handle metadata load event
    video.onloadedmetadata = () => {
      if (video.duration && !isNaN(video.duration)) {
        info.duration = video.duration.toFixed(2) + ' sec';
      } else {
        info.duration = 'Unknown';
      }
    };

    // Set the source using the base64 URL
    video.src = info.videoUrl;
  }

  async extractAudioMetadata(file: Blob | ArrayBuffer, info: any) {
    // Set default audio info immediately to ensure we always have baseline data
    info.audio = {
      Title: 'Unknown',
      Artist: 'Unknown',
      Duration: 'Calculating...'
    };

    // Extract duration using audio element as a first approach
    this.extractAudioDuration(info.audioUrl, info);

    // Try to extract detailed metadata if possible
    try {
      // Call parseBlob directly
      const metadata = await parseBlob(file instanceof ArrayBuffer ? new Blob([file]) : file);

      // Only update fields if we got valid data
      if (metadata && metadata.common) {
        if (metadata.common.title) {
          info.audio.Title = metadata.common.title;
        }

        if (metadata.common.artist) {
          info.audio.Artist = metadata.common.artist;
        }

        if (metadata.common.album) {
          info.audio.Album = metadata.common.album;
        }

        if (metadata.common.year) {
          info.audio.Year = metadata.common.year.toString();
        }

        // Use duration from metadata if available and we don't already have it
        if (metadata.format && metadata.format.duration &&
          info.audio.Duration === 'Calculating...') {
          info.audio.Duration = metadata.format.duration.toFixed(2) + ' sec';
        }
      }

      // Remove empty values
      Object.keys(info.audio).forEach(key => {
        if (!info.audio[key]) {
          delete info.audio[key];
        }
      });

    } catch (error) {
      console.warn('Error reading detailed audio metadata:', error);
      // We already have basic audio info from default values and duration extraction
    }
  }

  // Helper to extract duration using HTML5 audio element
  private extractAudioDuration(audioUrl: string, info: any) {
    const audio = document.createElement('audio');
    audio.preload = 'metadata';

    audio.onloadedmetadata = () => {
      if (audio.duration && !isNaN(audio.duration)) {
        info.audio.Duration = audio.duration.toFixed(2) + ' sec';
      }
    };

    audio.onerror = () => {
      console.warn('Audio duration extraction failed');
      info.audio.Duration = 'Unable to determine';
    };

    audio.src = audioUrl;
  }

  async extractWordText(file: ArrayBuffer, info: any) {
    try {
      const result = await extractRawText({ arrayBuffer: file });
      info.text = result.value.substring(0, 200) + '...';
    } catch (error) {
      console.warn('extractWordText: ', error);
      info.text = 'N/A';
    }
  }

  // TODO: think about how to resolve issues with created Excel files from Pyodide via pandas
  async extractExcelData(file: ArrayBuffer, info: any) {
    try {
      const workbook = XLSX.read(file, { type: 'array' });
      info.sheets = workbook.SheetNames.join(', ');
    } catch (error) {
      console.warn('extractExcelData: ', error);
      info.text = 'N/A';
    }
  }

  async extractUnknownFileInfo(buffer: Uint8Array, info: any) {
    const firstBytes = Array.from(buffer.slice(0, 16))
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join(' ');

    const textContent = this.extractReadableText(buffer);

    info.unknown = {
      message: 'Unknown file type. Extracted raw details:',
      firstBytes,
      readableText: textContent ? textContent.substring(0, 200) + '...' : 'N/A',
    };

    console.warn('Unknown file detected. Raw signature:', firstBytes);
  }

  extractReadableText(buffer: Uint8Array): string {
    let text = '';
    for (let i = 0; i < buffer.length; i++) {
      const char = String.fromCharCode(buffer[i]);
      if (char.match(/[a-zA-Z0-9\s,.!?]/)) {
        text += char;
      }
    }
    return text;
  }

  isImage(file: any): boolean {
    // Handle if file.type is a string (MIME type)
    if (typeof file.type === 'string') {
      return file.type.startsWith('image/');
    }

    // Handle file extensions if type is not available
    if (typeof file.name === 'string') {
      const extension = file.name.split('.').pop()?.toLowerCase();
      const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'tiff', 'tif', 'heic', 'heif'];
      return imageExtensions.includes(extension);
    }

    // If we have a string in FILE_TYPE format
    if (file.data && file.data.fileType) {
      return file.data.fileType === FILE_TYPE.image;
    }

    return false;
  }

  isExcel(file: any): boolean {
    // Check by MIME type
    if (typeof file.type === 'string') {
      const excelMimeTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.oasis.opendocument.spreadsheet',
        'application/vnd.ms-excel.sheet.macroEnabled.12'
      ];
      return excelMimeTypes.includes(file.type);
    }

    // Check by file extension
    if (typeof file.name === 'string') {
      const extension = file.name.split('.').pop()?.toLowerCase();
      const excelExtensions = ['xls', 'xlsx', 'xlsm', 'xlsb', 'csv', 'ods'];
      return excelExtensions.includes(extension);
    }

    // Check by FILE_TYPE constant
    if (file.data && file.data.fileType) {
      return file.data.fileType === file.data.fileType.includes('excel') || file.data.fileType.includes('spreadsheet');
    }

    return false;
  }

  isWord(file: any): boolean {
    // Check by MIME type
    if (typeof file.type === 'string') {
      const wordMimeTypes = [
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.oasis.opendocument.text',
        'application/vnd.ms-word.document.macroEnabled.12'
      ];
      return wordMimeTypes.includes(file.type);
    }

    // Check by file extension
    if (typeof file.name === 'string') {
      const extension = file.name.split('.').pop()?.toLowerCase();
      const wordExtensions = ['doc', 'docx', 'docm', 'odt', 'rtf'];
      return wordExtensions.includes(extension);
    }

    // Check by FILE_TYPE constant
    if (file.data && file.data.fileType) {
      return file.data.fileType.includes('word') || file.data.fileType.includes('document');
    }

    return false;
  }

  isVideo(file: any): boolean {
    // Check by MIME type
    if (typeof file.type === 'string') {
      const videoMimeTypes = [
        'video/mp4',
        'video/webm',
        'video/ogg',
        'video/quicktime',
        'video/x-msvideo',
        'video/x-matroska',
        'video/mpeg',
        'video/3gpp',
        'video/x-ms-wmv'
      ];
      return videoMimeTypes.includes(file.type);
    }

    // Check by file extension
    if (typeof file.name === 'string') {
      const extension = file.name.split('.').pop()?.toLowerCase();
      const videoExtensions = ['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv', 'mpg', 'mpeg', '3gp', 'wmv', 'flv', 'm4v'];
      return videoExtensions.includes(extension);
    }

    // Check by FILE_TYPE constant
    if (file.data && file.data.fileType) {
      return file.data.fileType === FILE_TYPE.video;
    }

    return false;
  }

  isAudio(file: any): boolean {
    // Check by MIME type
    if (typeof file.type === 'string') {
      const audioMimeTypes = [
        'audio/mpeg',
        'audio/mp3',
        'audio/wav',
        'audio/ogg',
        'audio/aac',
        'audio/flac',
        'audio/webm',
        'audio/x-m4a',
        'audio/x-ms-wma'
      ];
      return audioMimeTypes.includes(file.type);
    }

    // Check by file extension
    if (typeof file.name === 'string') {
      const extension = file.name.split('.').pop()?.toLowerCase();
      const audioExtensions = ['mp3', 'wav', 'ogg', 'aac', 'flac', 'wma', 'm4a', 'opus', 'aiff', 'alac'];
      return audioExtensions.includes(extension);
    }

    // Check by FILE_TYPE constant
    if (file.data && file.data.fileType) {
      return file.data.fileType === FILE_TYPE.audio;
    }

    return false;
  }
}
